import { useEffect, useState, useSyncExternalStore } from 'react';
import clsx from 'clsx';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { exportReport, type ExportCell } from '../exportReport';

/* Shared helpers of the account reports (the components are in reportParts.tsx). */

// ── Dates and amounts ──
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const today = () => ymd(new Date());
export const fyStartOf = (d: string) => (Number(d.slice(5, 7)) >= 4 ? `${d.slice(0, 4)}-04-01` : `${Number(d.slice(0, 4)) - 1}-04-01`);
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** 2026-11-02 → 2-Nov-26, as Tally prints dates. */
export const tdate = (d: string | null | undefined) => (d ? `${Number(d.slice(8, 10))}-${MON[Number(d.slice(5, 7)) - 1]}-${d.slice(2, 4)}` : '');
export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/** 1,23,456.00 — blank for nil. */
export const amt = (n: number | null | undefined) => (n && Math.abs(n) > 0.004 ? Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
/** + Dr / − Cr → "1,23,456.00 Dr". */
export const drcr = (n: number | null | undefined) => (n && Math.abs(n) > 0.004 ? `${amt(n)} ${n > 0 ? 'Dr' : 'Cr'}` : '');
/** Tally's negative in a statement: (-)1,234.00. */
export const neg = (n: number | null | undefined) => (n && Math.abs(n) > 0.004 ? `${n < 0 ? '(-)' : ''}${amt(n)}` : '');
export const r2 = (n: number) => Math.round(n * 100) / 100;

// ── One period for every report (as Tally's F2), remembered in this browser ──
export interface RPeriod { from: string; to: string }
const KEY = 'grhamoy.acc-report-period';
let current: RPeriod = (() => {
  try {
    const p = JSON.parse(localStorage.getItem(KEY) ?? 'null') as RPeriod | null;
    if (p && /^\d{4}-\d{2}-\d{2}$/.test(p.from) && /^\d{4}-\d{2}-\d{2}$/.test(p.to) && p.from <= p.to) return p;
  } catch { /* private window */ }
  return { from: fyStartOf(today()), to: today() };
})();
const subs = new Set<() => void>();
const setCurrent = (p: RPeriod) => {
  current = p;
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* ignore */ }
  subs.forEach((f) => f());
};
export function usePeriod(): [RPeriod, (p: RPeriod) => void] {
  const p = useSyncExternalStore((f) => { subs.add(f); return () => { subs.delete(f); }; }, () => current);
  return [p, setCurrent];
}

export const th = 'px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]';
export const td = 'px-2 py-1.5 align-top';
export const num = 'px-2 py-1.5 text-right font-mono tabular-nums whitespace-nowrap align-top';

/**
 * Keyboard as in Tally: ↑ ↓ move between the lines that open something, Enter opens, Esc goes back.
 * `acts[i]` is what line i opens (null = nothing).
 */
/** For a page whose lines are hidden (a drill-down is showing): nothing to move through. */
export const NO_ACTS: (null | (() => void))[] = [];

/** The first line that opens something. */
const first = (xs: (null | (() => void))[]) => Math.max(0, xs.findIndex(Boolean));

export function useKeyNav(acts: (null | (() => void))[], onBack?: () => void) {
  const [picked, setSel] = useState(() => first(acts));
  // A new level (new lines) starts on its first line that opens something.
  useEffect(() => { setSel(first(acts)); }, [acts]);
  // Never point past the lines shown (e.g. a level with fewer lines before the reset lands).
  const sel = acts[picked] ? picked : first(acts);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el?.closest('input,select,textarea,[role=dialog]') || document.querySelector('[role=dialog]')) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const d = e.key === 'ArrowDown' ? 1 : -1;
        let i = sel;
        do { i += d; } while (i >= 0 && i < acts.length && !acts[i]);
        if (i >= 0 && i < acts.length) setSel(i);
      } else if (e.key === 'Enter' && acts[sel]) { e.preventDefault(); acts[sel]!(); }
      else if (e.key === 'Escape' && onBack) { e.preventDefault(); onBack(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [acts, sel, onBack]);
  useEffect(() => { document.querySelector(`[data-rk="${sel}"]`)?.scrollIntoView({ block: 'nearest' }); }, [sel]);
  /** Props for line i. The mouse picks a line only when it really moves (not when lines re-render under a still pointer). */
  const row = (i: number, cls = '') => ({
    'data-rk': i,
    onMouseMove: (e: { movementX: number; movementY: number }) => { if ((e.movementX || e.movementY) && acts[i] && i !== sel) setSel(i); },
    onClick: acts[i] ?? undefined,
    className: clsx('border-b border-[var(--border-color)]', cls, acts[i] && 'cursor-pointer hover:bg-accent/5', acts[i] && i === sel && 'bg-accent/10'),
  });
  return { sel, row };
}

/** Excel of what the page shows. */
export function useExcel() {
  const toast = useToast();
  return (file: string, sheet: string, headers: string[], rows: ExportCell[][]) =>
    exportReport(file, sheet, headers, rows).catch((e) => toast.push(apiError(e, 'Export failed'), 'error'));
}

